import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts } from "../../../Styles";
import { CheckBox, CheckStyles, NewInput, NewInputList, NewSelect, StationLocationVisual, ModalLvl1 } from "../.."
import { ListStyle_Types, NewFieldStyles, NewFieldTypes, NewInputHandles, SelectFieldOption_Values, SystemTypes } from "../../../Definitions";
import { Station } from "../../../Definitions/newSectionModalInterfaces";
import { useModalContext } from "../../LocationFeeder/_LocationFeeder/newSectionModalContext";
import { StationLocationVisualWalkIn } from "../StationLayouts/esfStationVisualWalkIn";

// #region | Column Exit Options
// Double Arrow
export function DoubleArrow({ size = "20px", fillColor = colors.greyLightTertiary, locationVisual = 1 }) {
  let arrowTransform: string = "";

  switch (locationVisual) {
    case 2:
      arrowTransform = "scale(1, -1)"
    break
    case 3:
      arrowTransform = "scale(-1, 1)"
    break
    case 4:
      arrowTransform = "scale(-1, -1)"
    break
    case 1:
    default:
      arrowTransform = ""
    break
  } 

  return (
    <svg style={{transform: arrowTransform}} height={size} width={size} fill={fillColor} xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 283.46 283.46">  
      <path d="M79.273,49.463L136.232,140.052L150.706,111.257L248.75,160.537L269.527,119.201L171.482,69.921L185.956,41.126L79.273,49.463Z"/>
      <path d="M24.862,157.297L81.821,247.886L96.295,219.091L194.339,268.371L215.116,227.035L117.071,177.755L131.544,148.96L24.862,157.297Z"/>
    </svg>
  )
}

export function DoubleArrowWI({ size = "20px", fillColor = colors.greyLightTertiary }) {
  
  return (
    <svg height={size} width={size} fill={fillColor} xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 283.46 283.46">  
      <path d="M255.6 207.9L156.9 267.5L156.9 232.8L38.6 232.8L38.6 182.9L156.9 182.9L156.9 148.2L255.6 207.9Z" />
      <path d="M28 75.7L126.7 16L126.7 50.7L245 50.7L245 100.6L126.7 100.6L126.7 135.3L28 75.7Z" />
    </svg>
  )
}

// Options
const columnExitOptionsWF: SelectFieldOption_Values[] = [
  { id: 1, label: "top-left", icon: DoubleArrow({locationVisual: 1}) },
  { id: 2, label: "bottom-left", icon: DoubleArrow({locationVisual: 2}) },
  { id: 3, label: "top-right", icon: DoubleArrow({locationVisual: 3}) },
  { id: 4, label: "bottom-right", icon: DoubleArrow({locationVisual: 4}) },
];

const columnExitOptionsWI: SelectFieldOption_Values[] = [
  { id: 1, label: "left", icon: DoubleArrowWI({}) },
  { id: 2, label: "right", icon: DoubleArrowWI({}) },
];

const stationTypeOptions: SelectFieldOption_Values[] = [
  { id: 2, label: "Walk Through" },
  { id: 3, label: "Walk In" },
];
// #endregion

interface CheckedContent {
  feeder: number,
  water: boolean,
  button: boolean,
}

const NewEsfWfSectionModalLvl1: React.FC = () => {
  const { t } = useTranslation()
  const { state: { error, section }, dispatch } = useModalContext()
  const [ErrorText, setErrorText] = useState<string | undefined>(undefined);
  const nameRef = React.useRef<NewInputHandles>(null); 

  const stations = { ...section, contents: section.contents as Station[] };
  
  // Error handling
  useEffect(() => {
    if (!error) return;
    if (!error.errorText) setErrorText(undefined);
    else setErrorText(error.errorText);

    nameRef.current?.setError(error.Elementid === 1)
  }, [error])

  useEffect(() => {
    if (!section.contents.length) return;
    const startNum = Number(section.startNumber ?? 0);
    const updatedPens = section.contents.map((pen, index) => ({
      ...pen,
      name: String(startNum + index),
    }));
  
    dispatch({ section: { contents: updatedPens }});
  }, [section.startNumber]);
  
  // #region | Handle Input Fields
  const handleRowsChange = (e: any) => {
    dispatch({section:{rows:Number(e)}})
    dispatch({section:{contents:[]}})
  };

  const handleColsChange = (e: any) => {
    dispatch({section:{columns:Number(e)}})
    dispatch({section:{contents:[]}})
  };

  const handleStartNumberChange = (e: any) => {
    dispatch({ section: { startNumber: Number(e) } })
  };
  // #endregion

  // #region | Tableview functionality
  const totalCells = section.rows * section.columns;

  const handleCellClick = (stationId: number) => {
    const stationIdStr = String(stationId);
    const existingIndex = stations.contents.findIndex(s => s.stationId === stationIdStr);
  
    if (existingIndex !== -1) {
      const stationToRemove = section.contents[existingIndex];
      const removedNameNum = parseInt(stationToRemove.name ?? "", 10);
  
      let updated = stations.contents.filter(s => s.stationId !== stationIdStr);
  
      updated = updated.map(station => {
        const nameNum = parseInt(station.name ?? "", 10);
        if (nameNum > removedNameNum) {
          return { ...station, name: String(nameNum - 1) };
        }
        return station;
      });
      dispatch({section:{contents:updated}})
  
    } else {
      const existingNames = stations.contents.map(s => parseInt(s.name ?? "", 10)).filter(n => !isNaN(n));
      const maxName = existingNames.length ? Math.max(...existingNames) : (section.startNumber ?? 0) - 1;
      const newName = maxName + 1;
      const rowStart = Math.floor(stationId / section.columns) + 1;
      const colStart = (stationId % section.columns) + 1;
      const rowEnd = rowStart + 1;
      const colEnd = colStart + 1;
      const isFirstColumn = colStart === 1;
      const colVisuals = isFirstColumn ? section.firstColVisuals : section.secondColVisuals;
      const visual = `${rowStart}/${colStart}/${rowEnd}/${colEnd}-${section.rows}/${section.columns}-${colVisuals}`;
      const newStation: Station = { stationId: stationIdStr, visual, id: "", name: String(newName), reader: null, terminals: {} };

      dispatch({section:{contents:[...section.contents, newStation]}})
    }
  };
  
  const getCellNumber = (stationId: number) => {
    const index = stations.contents.findIndex(s => s.stationId === String(stationId));
  
    if (index !== -1) {
      const startNum = Number(section.startNumber ?? 0); 
      const calculatedNumber = startNum + index;
      return String(calculatedNumber);
    }
    return null;
  };
  // #endregion
  
  // #region | TableView
  const esf_walkthrough_stationType = (section.stationType === 2 || section.stationType === undefined) ? true : false;
  
  const tableView = (
    Array.from({ length: totalCells }).map((_, i) => {
      const isSelected = stations.contents.some(s => s.stationId === String(i));
      const number = getCellNumber(i);

      if (esf_walkthrough_stationType) {
      
      return (
        <StationLocationVisual
          key={i}
          content={number ?? ""}
          locationVisual={((i % section.columns) + 1) === 1 ? section.firstColVisuals ?? 0 : section.secondColVisuals ?? 0}
          onClick={() => handleCellClick(i)}
          isSelected={isSelected}
        />
      );
        
      } else {
        return (
          <StationLocationVisualWalkIn
            key={i}
            content={number ?? ""}
            locationVisual={((i % section.columns) + 1) === 1 ? section.firstColVisuals ?? 0 : section.secondColVisuals ?? 0}
            onClick={() => handleCellClick(i)}
            isSelected={isSelected}
          />
        );
      }
    })
  );
  // #endregion

  // #region | Handle Checkboxes
  const handleCheckboxChange = (name: keyof CheckedContent) => {

      dispatch({ section: { [name]: !section[name] }});
    }
  // #endregion
  console.log(section.gate)
  return (
    <ModalLvl1
      systemType={SystemTypes.freedaEsf_WalkThrough}
      onReset={() => dispatch({section:{contents:[]}})}
      penLength={section.contents.length}
      leftContent={<>
        {/* Section Name */}
        <NewInput
          ref={nameRef}
          placeholder=""
          header={t("esfWalkThroughPages.newSection.penName", { defaultValue: "pen name" })}
          isRequired
          onChange={(e) => dispatch({ section: { name: e } })}
          valueOverrides={{
            defaultValue: section.name
          }}
        />
        <NewSelect
          options={stationTypeOptions}
          header={t("", {defaultValue: "station type"})}
          onChange={(i) => { if (i) dispatch({ section: { stationType: Number(i.id), gate: i.id === 2 ? true : false } }) }}
          valueOverrides={{
            defaultValue: stationTypeOptions.find(e => e.id === section.stationType) ?? stationTypeOptions[0]
          }}
        />
        {/* Columns, Rows, First Station No. */}
        <NewInputList
          placeholders={["number", "number", "number"]}
          headers={[t("devices.columns", {defaultValue: "Columns"}), t("devices.rows", {defaultValue: "Rows"}), t("esfWalkThroughPages.newSection.firstStationNo", {defaultValue: "First station No."})]}
          onChanges={[handleColsChange, handleRowsChange, handleStartNumberChange]}
          valueOverrides={{
            defaultValues: [section.columns, section.rows, section.startNumber ?? 0],
            valueTypes: ["number", "number", "number"],
            maxValueNumbers: [2, undefined, undefined],
            minValueNumbers: [1, 1, 1]
          }}
          listOverrides={{
            listStyle: ListStyle_Types.vertical,
            listGap: "10px"
          }}
        />
        {/* Columns Exits */}
        <div style={{display: "flex", flexDirection: "column", gap: "10px"}}>
          <NewSelect
            options={esf_walkthrough_stationType ? columnExitOptionsWF : columnExitOptionsWI}
            startEnhancer={esf_walkthrough_stationType ? DoubleArrow({locationVisual: section.firstColVisuals}) : DoubleArrowWI({})}
            header={t("esfWalkThroughPages.newSection.firstColumnExit", {defaultValue: "first column exit"})}
            onChange={(i) => { if (i) dispatch({ section: { firstColVisuals: Number(i.id) } }) }}
            valueOverrides={{
              defaultValue: esf_walkthrough_stationType ? (columnExitOptionsWF.find(e => e.id === section.firstColVisuals) ?? columnExitOptionsWF[0])
                : (columnExitOptionsWI.find(e => e.id === section.firstColVisuals) ?? columnExitOptionsWI[0])
            }}
          />
          {section.columns === 2 && <NewSelect
            options={esf_walkthrough_stationType ? columnExitOptionsWF : columnExitOptionsWI}
            startEnhancer={esf_walkthrough_stationType ? DoubleArrow({locationVisual: section.secondColVisuals }) : DoubleArrowWI({})}
            header={t("esfWalkThroughPages.newSection.secondColumnExit", {defaultValue: "second column exit"})}
            onChange={(i) => { if (i) dispatch({ section: { secondColVisuals: Number(i.id) } }) }}
            valueOverrides={{
              defaultValue: esf_walkthrough_stationType ? (columnExitOptionsWF.find(e => e.id === section.secondColVisuals) ?? columnExitOptionsWF[0])
                : (columnExitOptionsWI.find(e => e.id === section.secondColVisuals) ?? columnExitOptionsWI[0])
            }}
          />}
        </div>
      </>}
      leftContentMobileHeight={section.columns === 2 ? 314 : 260}
      rightContent={
        <div style={{ display: "grid", gridTemplateRows: `repeat(${section.rows}, ${esf_walkthrough_stationType ? "37px" : "32px"})`, gridTemplateColumns: `repeat(${section.columns}, 118px)`, gap: esf_walkthrough_stationType ? "10px" : "15px" }}>
          {tableView}
        </div>
      }
      bottomContent={ <>
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: colors.blueDarkSecondary, padding: "6px 10px", gap: "10px", borderRadius: "10px" }}>
        <NewInput
          valueOverrides={{
            defaultValue: stations.feeder,
            valueType: "number"
          }}
          fieldOverrides={{
            fieldWidth: "60px",
            fieldStyle: NewFieldStyles.blueDarkPrimary,
            fieldType: NewFieldTypes.quaternaryField
          }}
          onChange={(e) => dispatch({ section: { feeder: Number(e) } })}
          />
          <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{t("esfWalkThroughPages.newSection.numberOfFeeders", {defaultValue: "Number of feeders"})}</p>
        </div>
        <CheckBox text={t("esfWalkThroughPages.newSection.water", {defaultValue: "Water"})} checkStyle={CheckStyles.blue} isChecked={section.water} onChange={() => handleCheckboxChange("water")}/> 
        <CheckBox text={t("esfWalkThroughPages.newSection.button", {defaultValue: "Button"})} isChecked={section.button} checkStyle={CheckStyles.blue} onChange={() => (handleCheckboxChange("button"))} />         
      </> }
    />
  );
}

export default NewEsfWfSectionModalLvl1
